<template>
  <q-linear-progress v-if="isLoading" indeterminate />
  <PForm
    v-else
    ref="pForm"
    :show-header="false"
    :primary-cta="{}"
    :secondary-cta="{}"
    :style="{ height: 'auto !important' }"
    body-class="q-px-2x-md q-pt-xl overflow-hidden"
  >
    <div
      v-for="({ document_type, documents }, index) in userDocuments"
      :key="index"
      class="q-mb-2x-xl"
    >
      <div class="col-12">
        <span class="text-md text-weight-medium text-neutral">
          {{ useChangeCase(document_type || "", "capitalCase").value }}
        </span>
        <q-separator />
      </div>
      <div class="col-12">
        <span class="text-xs text-neutral-70">
          Allowed file types are pdf, images, excel, csv
        </span>
      </div>
      <div v-for="(document, idx) in documents" :key="idx" class="q-mt-sm">
        <PFile
          ref="fileComponent"
          v-model="document.files"
          variant="compact"
          :label="useChangeCase(document.name || '', 'capitalCase').value"
          :required="document.required"
          :max-files="document.max_documents"
          :loading-state="getLoadingState(document.files)"
          :error="!!validationErrors[document._id]"
          defer-removal
          multiple
          accept=".pdf, .jpeg, .jpg, .png, .xlsx, .csv"
          error-message-class="text-xs"
          @update:model-value="
            (value) => onUpdateFile(document, value, index, idx)
          "
          @remove-file="(i, file) => removeFile(i, file, document, index, idx)"
        >
          <template #action-icon>
            <UploadIcon class="icon--md" />
          </template>
        </PFile>
      </div>
    </div>
  </PForm>
</template>
<script setup>
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import UploadIcon from "assets/icons/re-upload.svg";

const { userDocuments } = storeToRefs(useUserStore());

const { getUserDocuments, user } = useUserStore();
const { uploadOnboardingFile } = useFile();
const { removeOnboardingFile } = useFileStore();

const fileComponent = ref(null);

const validationErrors = reactive({});

const getLoadingState = (files) => {
  const state = {};

  if (isFileActionLoading.value) {
    for (const f of files ?? []) {
      if (!f._id && f.name) state[f.name] = true;
    }
  }

  if (removeLoadingFileId.value) {
    state[removeLoadingFileId.value] = true;
  }

  return state;
};

const removeLoadingFileId = ref(null);
const removeFile = async (
  idx,
  file,
  document,
  userDocumentIdx,
  documentIdx,
) => {
  try {
    removeLoadingFileId.value = file._id;

    const response = await removeOnboardingFile(file._id, document);

    const updatedDoc = {
      ...document,
      status_cd: response?.data?.data?.status_cd,
      files: response?.data?.data?.files,
    };

    unref(userDocuments)[userDocumentIdx].documents[documentIdx] = updatedDoc;

    const index = flatUserDocument.findIndex((d) => d._id == document._id);

    flatUserDocument[index] = updatedDoc;

    delete validationErrors[document._id];
  } catch (error) {
    console.error(error);
  } finally {
    removeLoadingFileId.value = null;
  }
};

const isFileActionLoading = ref(false);
const onUpdateFile = async (document, files, userDocumentIdx, documentIdx) => {
  if (files?.length) {
    delete validationErrors[document._id];
  }

  try {
    isFileActionLoading.value = true;

    const index = flatUserDocument.findIndex((d) => d._id == document._id);

    let filesToUpload = [];

    for (const file of files) {
      if (file._id) {
        continue;
      }

      filesToUpload.push(file);
    }

    if (filesToUpload.length) {
      const response = await uploadOnboardingFile({
        shop_name: unref(user).businessDetails.shop_name,
        type: document.code,
        category: document.category,
        file: filesToUpload,
        user_id: document.user_id,
        user_document_id: document._id,
      });

      unref(userDocuments)[userDocumentIdx].documents[documentIdx] = {
        ...document,
        status_cd: response?.data?.data?.status_cd,
        files: response?.data?.data?.files,
      };

      flatUserDocument[index] = {
        ...document,
        status_cd: response?.data?.data?.status_cd,
        files: response?.data?.data?.files,
      };
    }

    isFileActionLoading.value = false;
  } catch (error) {
    console.error(error);

    isFileActionLoading.value = false;
  }
};

const validateDocs = () => {
  let isValidated = true;

  Object.keys(validationErrors).forEach((k) => delete validationErrors[k]);

  for (const document of flatUserDocument) {
    if (document.required && !document.files?.length) {
      validationErrors[document._id] = true;

      isValidated = false;
    }
  }

  return isValidated;
};

defineExpose({
  validateDocs,
});

let flatUserDocument = [];

const isLoading = ref(false);

onBeforeMount(async () => {
  isLoading.value = true;

  await getUserDocuments({ groupBySubCategories: 1 });

  for (const doc of unref(userDocuments)) {
    flatUserDocument.push(doc.documents);
  }

  flatUserDocument = flatUserDocument.flat();

  isLoading.value = false;
});
</script>
